import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { db } from "../services/firebase";
import {
  doc,
  setDoc,
  onSnapshot,
} from "firebase/firestore";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import { useProducts } from "./ProductsContext";

export const CartContext = createContext();

export function CartProvider({ children }) {
  const { user } = useAuth();
  const { showToast } = useToast();
  // M-7: live stock map from ProductsContext so increaseQty / addToCart
  // always cap against current Firestore stock, not the stale snapshot
  // that was baked into the cart item when it was first added.
  const { products } = useProducts();
  const liveStockMap = useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p.stock])),
    [products]
  );
  const [cart, setCart] = useState([]);
  const saveTimerRef = useRef(null); // Phase 1 fix: debounce cart writes

  /* ---------------- FIRESTORE SYNC ---------------- */
  // Load cart from Firestore when user logs in
  useEffect(() => {
    if (!user) {
      setCart([]); // clear cart on logout
      return;
    }

    const cartRef = doc(db, "carts", user.uid);

    const unsub = onSnapshot(cartRef, (snap) => {
      if (snap.exists()) {
        setCart(snap.data().items || []);
      } else {
        setCart([]);
      }
    });

    return () => unsub();
  }, [user]);

  // Phase 1 fix: debounce Firestore writes — only the final state after rapid taps is written.
  // Cuts cart writes by 60-80% for typical users (was: 1 write per tap).
  const saveCart = useCallback((items) => {
    if (!user) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await setDoc(doc(db, "carts", user.uid), { items });
      } catch (err) {
        console.error("Cart save error:", err);
        showToast("Failed to save cart. Check your connection.", "error");
      }
    }, 1500);
  }, [user, showToast]);

  /* ---------------- ADD TO CART ---------------- */
  // I5: `options.mode` is "pcs" (default) or "box". When "box", each tap adds
  // a full box worth of pieces (product.pcsPerBox) instead of 1 piece, and the
  // cart entry remembers orderMode + pcsPerBox so the UI can show "2 box"
  // instead of "60 pcs". `qty` on the cart item is ALWAYS the effective piece
  // count — this keeps totalPrice, taxUtils, the invoice PDF, and admin stats
  // working unchanged, since they all assume qty × price = line total in pieces.
  const addToCart = (product, options = {}) => {
    // M-10 fix: guard against soft-deleted products — deleted:true + stock:0 are both
    // set during soft-delete, so the stock check below already blocks most cases.
    // The explicit deleted guard is belt-and-suspenders for any race condition.
    if (product.deleted === true) return;
    // M-7: use live stock from ProductsContext, fall back to product.stock if somehow
    // the product isn't in the live map yet (e.g. first render race).
    const liveStock = product.id in liveStockMap ? liveStockMap[product.id] : product.stock;
    // Guard against out-of-stock (numeric 0 or boolean false)
    if (liveStock === false || liveStock === 0) return;

    const orderMode = options.mode === "box" ? "box" : "pcs";
    const pcsPerBox = Number(product.pcsPerBox) || 1;
    const step = orderMode === "box" && pcsPerBox > 1 ? pcsPerBox : 1;

    setCart((prev) => {
      const existing = prev.find((item) => item.id === product.id);
      // M-7: also cap at live stock when incrementing via addToCart
      if (existing && typeof liveStock === "number" && liveStock > 0 && existing.qty + step > liveStock) {
        return prev; // adding another step would exceed live stock, don't add
      }
      // I4: explicitly carry hsn and gstRate so the invoice PDF always has
      // correct per-line HSN and GST rate, even if the product doc changes later.
      const cartEntry = {
        ...product,
        qty:        step,
        orderMode,
        pcsPerBox:  step > 1 ? pcsPerBox : null, // Firestore setDoc rejects `undefined`, so use null not step>1?pcsPerBox:undefined
        hsn:        product.hsnCode || product.hsn || "19059090",
        gstRate:    product.gstRate != null ? Number(product.gstRate) : 18,
      };
      const updated = existing
        ? prev.map((item) =>
            item.id === product.id
              ? { ...item, qty: item.qty + step }
              : item
          )
        : [...prev, cartEntry];

      saveCart(updated);
      return updated;
    });
  };

  /* ---------------- INCREASE QTY ---------------- */
  // I5: `step` defaults to 1 piece. ProductCard passes the cart item's own
  // pcsPerBox as the step when that item is in "box" mode, so +/- always
  // moves a whole box at a time once an item was added as a box.
  const increaseQty = (id, step = 1) => {
    setCart((prev) => {
      const item = prev.find((i) => i.id === id);
      if (!item) return prev;
      // M-7: look up live stock from ProductsContext rather than the stale
      // snapshot value baked into the cart item at add-time. Falls back to
      // item.stock only if the product has somehow left the live catalog.
      const liveStock = id in liveStockMap ? liveStockMap[id] : item.stock;
      // Cap at live numeric stock quantity
      if (typeof liveStock === "number" && liveStock > 0 && item.qty + step > liveStock) return prev;

      const updated = prev.map((i) =>
        i.id === id ? { ...i, qty: i.qty + step } : i
      );
      saveCart(updated);
      return updated;
    });
  };

  /* ---------------- DECREASE QTY ---------------- */
  const decreaseQty = (id, step = 1) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.id === id);
      if (!existing) return prev;

      const updated =
        existing.qty <= step
          ? prev.filter((item) => item.id !== id)
          : prev.map((item) =>
              item.id === id ? { ...item, qty: item.qty - step } : item
            );

      saveCart(updated);
      return updated;
    });
  };

  /* ---------------- REMOVE ITEM ---------------- */
  const removeItem = (id) => {
    setCart((prev) => {
      const updated = prev.filter((item) => item.id !== id);
      saveCart(updated);
      return updated;
    });
  };

  const replaceCartItems = useCallback((items = []) => {
    const nextItems = Array.isArray(items) ? items : [];
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setCart(nextItems);
    if (user) {
      setDoc(doc(db, "carts", user.uid), { items: nextItems }).catch((err) => {
        console.error("Cart refresh error:", err);
        showToast("Cart was refreshed, but saving it failed. Check your connection.", "error");
      });
    }
  }, [user, showToast]);

  /* ---------------- SET ORDER MODE (I5) ---------------- */
  // Lets a retailer convert an existing cart line between "box" and "pcs"
  // display WITHOUT removing/re-adding it. `qty` (always stored in pieces)
  // is untouched — this only changes the label and what the +/- step uses.
  const setOrderMode = (id, mode) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.id === id);
      if (!existing) return prev;
      const updated = prev.map((item) =>
        item.id === id ? { ...item, orderMode: mode === "box" ? "box" : "pcs" } : item
      );
      saveCart(updated);
      return updated;
    });
  };

  /* ---------------- SET EXACT QTY (I5) ---------------- */
  // Lets a retailer type an exact piece count directly — e.g. 30 pcs even
  // though the box size for that product is 24 — instead of only being able
  // to step by 1 piece or by a whole box. `pieceQty` is always in pieces,
  // regardless of which mode the line is currently displayed in.
  const setQty = (id, pieceQty) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.id === id);
      if (!existing) return prev;

      const liveStock = id in liveStockMap ? liveStockMap[id] : existing.stock;
      let qty = Math.max(0, Math.floor(Number(pieceQty) || 0));
      if (typeof liveStock === "number" && liveStock > 0) {
        qty = Math.min(qty, liveStock);
      }

      const updated = qty <= 0
        ? prev.filter((item) => item.id !== id)
        : prev.map((item) => (item.id === id ? { ...item, qty } : item));

      saveCart(updated);
      return updated;
    });
  };

  /* ---------------- CLEAR CART ---------------- */
  const clearCart = () => {
    // M-3 fix: cancel any pending debounced save — otherwise the 1500ms
    // timer (capturing the old cart state) can fire after clearCart()
    // and overwrite the now-empty cart back in Firestore.
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setCart([]);
    if (user) {
      setDoc(doc(db, "carts", user.uid), { items: [] }).catch((err) =>
        console.error("Cart clear error:", err)
      );
    }
  };

  /* ---------------- DERIVED VALUES ---------------- */
  const totalPrice = useMemo(
    () => cart.reduce((sum, item) => sum + (item.price || 0) * item.qty, 0),
    [cart]
  );

  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + item.qty, 0),
    [cart]
  );

  return (
    <CartContext.Provider
      value={{
        cart,
        cartCount,
        totalPrice,
        addToCart,
        increaseQty,
        decreaseQty,
        setOrderMode,
        setQty,
        removeItem,
        replaceCartItems,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

/* ---------------- useCart HOOK ---------------- */
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
